import { computed } from 'vue';
import { type HipSpineSafeZone } from '@/domain/anatomy/pelvis/hipSpineNomogramCalculations';
import type { HipSpineStoreContext } from './hipSpineStore';

/**
 * Build the hip-spine store's derived state (computed refs + `isPosturePending`)
 * over a shared {@link HipSpineStoreContext}. Extracted from the store body so the
 * `defineStore` setup callback stays within the function-length cap; behaviour is
 * identical to the inline computeds it replaces.
 */
export function createHipSpineStoreGetters(ctx: HipSpineStoreContext) {
    const { state } = ctx;

    const hasStudy = computed(() => state.hipSpineStudy !== null);

    const studyState = computed(() => state.hipSpineStudy?.resource_state ?? null);
    const isStudyCompleted = computed(() => studyState.value === 'Completed');
    const isStudyProcessing = computed(
        () => studyState.value === 'Created' || studyState.value === 'Processing',
    );
    const isStudyError = computed(() => studyState.value === 'Error');

    const pendingPostureCount = computed(
        () => state.hipSpinePostures.filter((posture) => posture.has_unapproved_changes).length,
    );
    const totalPostureCount = computed(() => state.hipSpinePostures.length);
    const allPosturesApproved = computed(
        () => pendingPostureCount.value === 0 && state.hipSpinePostures.length > 0,
    );
    // True when every posture has an approved landmark snapshot — independent of
    // whether a posture currently has unapproved (saved-but-not-yet-approved) edits.
    // The approved snapshot is static (only updated on approve), so the hip-spine
    // analysis stays visible across new edits and refreshes only once those new
    // changes are themselves approved. Gates analysis visibility (vs allPosturesApproved,
    // which would hide the analysis the moment an edit is saved). WI-687.
    const allPosturesHaveApprovedLandmarks = computed(
        () =>
            state.hipSpinePostures.length > 0 &&
            state.hipSpinePostures.every((posture) =>
                state.approvedLandmarks.some((approved) => approved.posture === posture.posture),
            ),
    );

    const hipSpineSafeZone = computed<HipSpineSafeZone | null>(() => {
        if (!state.hipSpineStudy) return null;
        return {
            minAnteversion: state.hipSpineStudy.min_cup_anteversion_bound,
            maxAnteversion: state.hipSpineStudy.max_cup_anteversion_bound,
            minInclination: state.hipSpineStudy.min_cup_inclination_bound,
            maxInclination: state.hipSpineStudy.max_cup_inclination_bound,
        };
    });

    function isPosturePending(postureId: string): boolean {
        const posture = state.hipSpinePostures.find((candidate) => candidate.posture === postureId);
        return posture?.has_unapproved_changes ?? false;
    }

    return {
        hasStudy,
        studyState,
        isStudyCompleted,
        isStudyProcessing,
        isStudyError,
        pendingPostureCount,
        totalPostureCount,
        allPosturesApproved,
        allPosturesHaveApprovedLandmarks,
        hipSpineSafeZone,
        isPosturePending,
    };
}
